<template>
  <div class="timeline-calendar-activity-slot ml-3">
    <div
      class="calendar-activity-card cursor-pointer"
      @click="$emit('toggle')"
    >
      <div class="calendar-activity-card__header">
        <div class="calendar-activity-card__avatar">
          <img
            v-if="hasPhoto"
            :src="photoSrc"
            alt=""
            class="calendar-activity-card__avatar-img"
          />
          <span v-else>{{ personInitials }}</span>
        </div>
        <div class="calendar-activity-card__header-text">
          <div class="calendar-activity-card__title-row">
            <span class="calendar-activity-card__name">{{ displayName }}</span>
            <span class="calendar-activity-card__badge">
              <i class="icon-eye" aria-hidden="true"></i>
              {{ $t('lang.opportunities.timeline_calendar_interest_badge') }}
            </span>
          </div>
          <div class="calendar-activity-card__email">{{ primaryActivity.email }}</div>
        </div>
        <div class="calendar-activity-card__time">
          <i class="icon-clock" aria-hidden="true"></i>
          {{ headerDateLabel }}
        </div>
      </div>

      <div class="calendar-activity-card__summary">
        <div class="calendar-activity-card__summary-icon">
          <i class="icon-calendar" aria-hidden="true"></i>
        </div>
        <div class="calendar-activity-card__summary-text">
          <div class="calendar-activity-card__summary-title">
            {{ $t('lang.opportunities.timeline_calendar_summary_title') }}
          </div>
          <div class="calendar-activity-card__summary-sub">
            {{ $t('lang.opportunities.timeline_calendar_summary_sub') }}
          </div>
        </div>
      </div>

      <div class="calendar-activity-card__stats">
        <div class="calendar-activity-card__stat">
          <div class="calendar-activity-card__stat-value">{{ uniqueDaysCount }}</div>
          <div class="calendar-activity-card__stat-label">{{ $t('lang.opportunities.timeline_calendar_days_viewed') }}</div>
        </div>
        <div class="calendar-activity-card__stat">
          <div class="calendar-activity-card__stat-value">{{ durationLabel }}</div>
          <div class="calendar-activity-card__stat-label">{{ $t('lang.opportunities.timeline_calendar_duration') }}</div>
        </div>
      </div>

      <div v-if="dayChips.length" class="calendar-activity-card__days">
        <div class="calendar-activity-card__days-title">
          <i class="icon-calendar" aria-hidden="true"></i>
          {{ $t('lang.opportunities.timeline_calendar_slots_viewed') }}
        </div>
        <div class="calendar-activity-card__days-grid">
          <span
            v-for="chip in dayChips"
            :key="chip.dayKey"
            class="calendar-activity-card__day-chip"
            :class="{
              'calendar-activity-card__day-chip--highlight': chip.count > 1,
            }"
            :title="chip.tooltip"
          >
            <i class="icon-calendar" aria-hidden="true"></i>
            <span class="calendar-activity-card__day-chip-date">{{ chip.label }}</span>
            <span
              v-if="chip.count > 1"
              class="calendar-activity-card__day-chip-count"
            >
              {{ chip.count }}
            </span>
          </span>
        </div>
      </div>

      <div class="calendar-activity-card__footer">
        <i class="icon-info-circle" aria-hidden="true"></i>
        {{ $t('lang.opportunities.timeline_calendar_activity_detected', { date: footerDateLabel }) }}
        · {{ $t('lang.opportunities.meeting_card_timezone', { zone: calendarTimezone }) }}
      </div>
    </div>
  </div>
</template>

<script>
import { formattersMixin } from "@/_mixins/";

const WEEKDAY_LABEL_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default {
  name: "TimelineCalendarActivityCard",
  mixins: [formattersMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    primaryActivity() {
      if (
        this.item &&
        Array.isArray(this.item.activities) &&
        this.item.activities.length
      ) {
        return this.item.activities[0];
      }
      return this.item || {};
    },
    visibleActivities() {
      if (!Array.isArray(this.item.activities)) {
        return [];
      }
      return this.item.activities.filter(
        (activity) => activity.link !== "Home" && activity.event_date
      );
    },
    displayName() {
      const name = this.primaryActivity.name;
      const email = this.primaryActivity.email;
      if (this.$options.filters && this.$options.filters.formatContactName) {
        return this.$options.filters.formatContactName(name, email);
      }
      return name || "";
    },
    personInitials() {
      return this.getPersonInitials(this.displayName);
    },
    hasPhoto() {
      const photo = this.item.photo || this.primaryActivity.photo;
      return photo != null && String(photo).trim() !== "";
    },
    photoSrc() {
      const photo = this.item.photo || this.primaryActivity.photo;
      return this.getBusinessPhoto(photo);
    },
    creationDate() {
      return this.parseDateString(
        this.primaryActivity.creation_date || this.item.creation_date
      );
    },
    headerDateLabel() {
      return this.formatHeaderDate(this.creationDate);
    },
    footerDateLabel() {
      return this.formatFooterDate(this.creationDate);
    },
    durationLabel() {
      const raw = this.primaryActivity.comment || this.item.comment || "";
      const seconds = this.parseDurationToSeconds(raw);
      if (seconds === null) {
        return raw;
      }
      return `${seconds}s`;
    },
    uniqueDaysCount() {
      return this.dayChips.length;
    },
    dayChips() {
      const groups = {};

      this.visibleActivities.forEach((activity) => {
        const parsed = this.parseDateString(activity.event_date);
        if (!parsed) {
          return;
        }
        const dayKey = this.getDayKey(parsed);
        if (!groups[dayKey]) {
          groups[dayKey] = {
            dayKey,
            date: parsed,
            eventDates: [],
          };
        }
        groups[dayKey].eventDates.push(activity.event_date);
      });

      return Object.values(groups)
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map((group) => ({
          dayKey: group.dayKey,
          label: this.formatChipDate(group.date),
          count: group.eventDates.length,
          tooltip: group.eventDates
            .slice()
            .sort((a, b) => this.parseDateString(b) - this.parseDateString(a))
            .join("\n"),
        }));
    },
    calendarTimezone() {
      return "Europe/Paris";
    },
  },
  methods: {
    getPersonInitials(name) {
      if (!name || typeof name !== "string") {
        return "?";
      }
      const trimmed = name.trim();
      if (!trimmed || trimmed.indexOf("@") !== -1) {
        return "?";
      }
      return trimmed
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    parseDateString(stringDate) {
      if (!stringDate) {
        return null;
      }
      const dateArray = String(stringDate).split(/-|\s|:/);
      const date = new Date(
        dateArray[0],
        dateArray[1] - 1,
        dateArray[2],
        dateArray[3] || 0,
        dateArray[4] || 0,
        dateArray[5] || 0
      );
      return isNaN(date.getTime()) ? null : date;
    },
    getDayKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatHeaderDate(date) {
      if (!date) {
        return "";
      }
      const day = date.getDate();
      const month = this.$t(
        `lang.opportunities.timeline_calendar_month_abbr_${date.getMonth() + 1}`
      );
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${hours}:${minutes}`;
    },
    formatFooterDate(date) {
      if (!date) {
        return "";
      }
      const day = date.getDate();
      const month = this.$t(
        `lang.opportunities.timeline_email_month_${date.getMonth() + 1}`
      );
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    },
    formatChipDate(date) {
      const dayName = this.$t(
        `lang.label.${WEEKDAY_LABEL_KEYS[date.getDay()]}`
      );
      const day = date.getDate();
      const month = this.$t(
        `lang.opportunities.timeline_calendar_month_abbr_${date.getMonth() + 1}`
      );
      return `${dayName} ${day} ${month}`;
    },
    parseDurationToSeconds(raw) {
      if (!raw || typeof raw !== "string") {
        return null;
      }
      const parts = raw.trim().split(":").map((part) => parseInt(part, 10));
      if (parts.length !== 3 || parts.some((part) => isNaN(part))) {
        return null;
      }
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    },
  },
};
</script>

<style scoped>
.timeline-calendar-activity-slot {
  min-width: 0;
}

.calendar-activity-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.calendar-activity-card__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #f4f4f7;
}

.calendar-activity-card__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ccfbf1;
  color: #115e59;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  overflow: hidden;
}

.calendar-activity-card__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.calendar-activity-card__header-text {
  flex: 1;
  min-width: 0;
}

.calendar-activity-card__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.calendar-activity-card__name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  line-height: 1.3;
}

.calendar-activity-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  background: #ccfbf1;
  color: #115e59;
  white-space: nowrap;
}

.calendar-activity-card__email {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-activity-card__time {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.calendar-activity-card__summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #ccfbf1;
  border-bottom: 1px solid #f4f4f7;
}

.calendar-activity-card__summary-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #fff;
  color: #115e59;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.calendar-activity-card__summary-title {
  font-size: 12px;
  font-weight: 600;
  color: #115e59;
  line-height: 1.3;
}

.calendar-activity-card__summary-sub {
  font-size: 11px;
  color: #115e59;
  opacity: 0.8;
  margin-top: 1px;
}

.calendar-activity-card__stats {
  display: flex;
  border-bottom: 1px solid #f4f4f7;
}

.calendar-activity-card__stat {
  flex: 1;
  padding: 10px 14px;
  text-align: center;
}

.calendar-activity-card__stat + .calendar-activity-card__stat {
  border-left: 1px solid #f4f4f7;
}

.calendar-activity-card__stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  line-height: 1.1;
}

.calendar-activity-card__stat-label {
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.calendar-activity-card__days {
  padding: 12px 14px;
}

.calendar-activity-card__days-title {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.calendar-activity-card__days-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.calendar-activity-card__day-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fafafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
}

.calendar-activity-card__day-chip--highlight {
  background: #f7f7fe;
  border-color: #eeeefc;
}

.calendar-activity-card__day-chip-date {
  font-weight: 500;
  color: #111827;
}

.calendar-activity-card__day-chip-count {
  font-size: 10px;
  font-weight: 600;
  color: #4b4be1;
  background: #fff;
  border: 1px solid #eeeefc;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 16px;
}

.calendar-activity-card__day-chip i {
  font-size: 13px;
  color: #9ca3af;
}

.calendar-activity-card__footer {
  padding: 8px 14px;
  background: #fafafb;
  border-top: 1px solid #f4f4f7;
  font-size: 10px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 5px;
}
</style>
